import React, { useEffect, useState, useRef, lazy, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { RestaurantHeader } from '../components/restaurant/RestaurantHeader';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { MenuParentTabs } from '../components/restaurant/MenuParentTabs';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData, useMarketplaceData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, Plus, ArrowDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuItem, CartItemOption, MenuCategory } from '../lib/types';
import { formatCurrency } from '../lib/utils';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useOpenOrderCheck } from '../lib/open-order-check';
import { OpenOrderAlert } from '../components/restaurant/OpenOrderAlert';
// ─── Category Mapping Config ────────────────────────────────────────────────
const PARENT_CATEGORIES = [{
  key: 'comida',
  label: 'Comida',
  icon: '🍽️',
  subcategories: ['despertares del valle', 'sabores del mediodía', 'ensaladas', 'platos con memoria', 'de la plancha al corazón']
}, {
  key: 'bebidas',
  label: 'Bebidas',
  icon: '🥤',
  subcategories: ['bebidas calientes a base de café', 'bebidas calientes', 'bebidas frías a base de café', 'bebidas frías', 'shakes y smoothies', 'funcionales', 'bubble tea', 'cervezas', 'vinos', 'cócteles', 'mócteles', 'alcohol']
}, {
  key: 'postres',
  label: 'Postres',
  icon: '🍰',
  subcategories: ['postres']
}, {
  key: 'ninos',
  label: 'Niños',
  icon: '👶',
  subcategories: ['para los pequeños']
}];
function getParentKeyForCategory(categoryName: string): string {
  const nameLower = categoryName.toLowerCase().trim();
  for (const parent of PARENT_CATEGORIES) {
    if (parent.subcategories.includes(nameLower)) {
      return parent.key;
    }
  }
  return 'comida';
}
export function RestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    settings,
    loading: restaurantLoading,
    error
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    zones
  } = useMarketplaceData();
  const {
    addItem
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    openOrder,
    shouldAlert,
    dismiss: dismissOpenOrder
  } = useOpenOrderCheck(restaurant?.id);
  const [showOpenOrderAlert, setShowOpenOrderAlert] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{
    item: MenuItem;
    quantity: number;
    options: CartItemOption[];
    notes: string;
  } | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeParent, setActiveParent] = useState<string>('comida');
  const [activeSubcategory, setActiveSubcategory] = useState<string>('');
  const menuSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (restaurant?.id) {
      monitorRestaurant(restaurant.id);
    }
  }, [restaurant?.id, monitorRestaurant]);
  // ─── Map Categories to Parents ─────────────────────────────────────────
  const categoriesWithItems = categories.filter(cat => items.some(item => item.category_id === cat.id));
  const parentGroups = PARENT_CATEGORIES.map(parent => {
    const subs = categoriesWithItems.filter(cat => getParentKeyForCategory(cat.name) === parent.key);
    const itemCount = items.filter(item => subs.some(sub => sub.id === item.category_id)).length;
    return {
      ...parent,
      subcategories: subs,
      itemCount
    };
  }).filter(p => p.itemCount > 0);
  useEffect(() => {
    if (parentGroups.length > 0 && !parentGroups.find(p => p.key === activeParent)) {
      const firstParent = parentGroups[0];
      setActiveParent(firstParent.key);
      if (firstParent.subcategories.length > 0) {
        setActiveSubcategory(firstParent.subcategories[0].id);
      }
    } else if (parentGroups.length > 0) {
      const currentParent = parentGroups.find(p => p.key === activeParent);
      if (currentParent && currentParent.subcategories.length > 0 && !activeSubcategory) {
        setActiveSubcategory(currentParent.subcategories[0].id);
      }
    }
  }, [parentGroups, activeParent, activeSubcategory]);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    if (isKitchenPaused) {
      alert('La cocina está pausada en este momento. Vuelve en 10 minutos.');
      return;
    }
    // Show open order alert on first add attempt
    if (shouldAlert) {
      console.log('[OpenOrderCheck] Intercepting add-to-cart — showing alert');
      setPendingAdd({
        item,
        quantity,
        options,
        notes
      });
      setShowOpenOrderAlert(true);
      return;
    }
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  const handleDismissOpenOrder = () => {
    dismissOpenOrder();
    setShowOpenOrderAlert(false);
    // Process the pending add
    if (pendingAdd && restaurant) {
      const {
        item,
        quantity,
        options,
        notes
      } = pendingAdd;
      const success = addItem({
        menu_item_id: item.id,
        restaurant_id: restaurant.id,
        item_name_snapshot: item.name,
        unit_price_snapshot: item.price,
        quantity,
        notes,
        image_url: item.image_url,
        selected_options: options,
        station_code: item.station_code || null
      }, restaurant.name);
      if (!success) {
        alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
      }
      setPendingAdd(null);
    }
  };
  const handleCancelOpenOrder = () => {
    setShowOpenOrderAlert(false);
    setPendingAdd(null);
  };
  const handleSelectParent = (parentKey: string) => {
    setActiveParent(parentKey);
    const parent = parentGroups.find(p => p.key === parentKey);
    if (parent && parent.subcategories.length > 0) {
      setActiveSubcategory(parent.subcategories[0].id);
    }
    scrollToMenu();
  };
  const handleSelectSubcategory = (categoryId: string) => {
    setActiveSubcategory(categoryId);
  };
  const scrollToMenu = () => {
    if (menuSectionRef.current) {
      const y = menuSectionRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // ─── Loading ───
  if (restaurantLoading) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-4362">
        <MarketplaceHeader data-id="element-4363" />
        <div className="h-48 sm:h-64 w-full bg-[#141414] animate-pulse" data-id="element-4364" />
        <div className="max-w-3xl mx-auto px-4 w-full mt-10 space-y-4" data-id="element-4365">
          <div className="h-8 w-48 bg-[#1a1a1a] rounded-lg animate-pulse" data-id="element-4366" />
          <div className="h-4 w-72 bg-[#1a1a1a] rounded animate-pulse" data-id="element-4367" />
          <div className="space-y-3 mt-8" data-id="element-4368">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-20 rounded-xl bg-[#1a1a1a] animate-pulse" data-id="element-4369" />)}
          </div>
        </div>
      </div>;
  }
  // ─── Error ───
  if (error || !restaurant) {
    return <div className="min-h-screen flex flex-col bg-[#0B0B0B]" data-id="element-4370">
        <MarketplaceHeader data-id="element-4371" />
        <div className="flex-1 flex items-center justify-center" data-id="element-4372">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible en este momento." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-4373" />
        </div>
        <Footer data-id="element-4374" />
      </div>;
  }
  const zoneName = zones.find(z => z.id === restaurant.zone_id)?.name;
  const highlightedItems = items.slice(0, 3);
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isKabu = restaurant.slug === 'kabu';
  const primaryColor = isEmilius ? '#D42B2B' : isKabu ? '#D4A853' : restaurant.primary_color || '#F59E0B';
  const activeParentData = parentGroups.find(p => p.key === activeParent);
  return <div className="min-h-screen flex flex-col bg-[#0B0B0B] pb-24" data-id="element-4375">
      <MarketplaceHeader data-id="element-4376" />

      <main className="flex-1" data-id="element-4377">
        <RestaurantHeader restaurant={restaurant} settings={settings} zoneName={zoneName} data-id="element-4378" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-4379">
          {/* ─── Highlighted Items — editorial feature cards ─── */}
          {highlightedItems.length > 0 && <section className="mb-10 sm:mb-14" data-id="element-4380">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/30 mb-4" data-id="element-4381">
                Recomendados
              </p>
              <div className="space-y-1" data-id="element-4382">
                {highlightedItems.map(item => <div key={item.id} onClick={() => handleProductClick(item)} className="group flex items-center gap-4 py-3.5 cursor-pointer border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors rounded-lg" data-id="element-4383">
                    <img src={item.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=300&fit=crop&q=60'} alt={item.name} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" data-id="element-4384" />
                    <div className="flex-1 min-w-0" data-id="element-4385">
                      <h3 className="font-semibold text-white/90 text-[15px] leading-tight line-clamp-1 group-hover:text-white transition-colors" data-id="element-4386">
                        {item.name}
                      </h3>
                      {item.description && <p className="text-[12px] text-white/30 line-clamp-1 mt-0.5" data-id="element-4387">
                          {item.description}
                        </p>}
                    </div>
                    <span className="font-semibold text-sm shrink-0" style={{
                color: primaryColor
              }} data-id="element-4388">
                      {formatCurrency(item.price)}
                    </span>
                  </div>)}
              </div>
            </section>}

          {/* ─── Parent Category Selector — minimal inline pills ─── */}
          <section className="mb-10 sm:mb-14" data-id="element-4389">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/30 mb-5" data-id="element-4390">
              Explorar
            </p>
            {menuLoading ? <div className="flex gap-3" data-id="element-4391">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-16 w-28 rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-4392" />)}
              </div> : parentGroups.length > 0 ? <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-1" data-id="element-4393">
                {parentGroups.map(parent => {
              const isActive = activeParent === parent.key;
              return <button key={parent.key} onClick={() => handleSelectParent(parent.key)} className="shrink-0 flex flex-col items-center gap-2 px-6 py-4 rounded-2xl transition-all active:scale-95" style={{
                backgroundColor: isActive ? `${primaryColor}15` : '#141414',
                borderWidth: 1,
                borderColor: isActive ? `${primaryColor}40` : 'rgba(255,255,255,0.05)'
              }} data-id="element-4394">
                      <span className="text-2xl" data-id="element-4395">{parent.icon}</span>
                      <span className={`text-xs font-semibold ${isActive ? 'text-white' : 'text-white/50'}`} data-id="element-4396">
                        {parent.label}
                      </span>
                    </button>;
            })}
              </div> : <p className="text-white/30 text-sm" data-id="element-4397">
                No hay categorías disponibles.
              </p>}
          </section>

          {/* ─── Subtle divider with scroll hint ─── */}
          <div className="flex items-center gap-3 mb-8" data-id="element-4398">
            <div className="flex-1 h-px bg-white/5" data-id="element-4399" />
            <button onClick={scrollToMenu} className="flex items-center gap-1.5 text-white/25 hover:text-white/40 transition-colors" data-id="element-4400">
              <span className="text-[11px] font-medium uppercase tracking-widest" data-id="element-4401">
                Menú
              </span>
              <ArrowDown className="w-3 h-3" data-id="element-4402" />
            </button>
            <div className="flex-1 h-px bg-white/5" data-id="element-4403" />
          </div>

          {/* ═══ FULL MENU SECTION ═══ */}
          <div ref={menuSectionRef} data-id="element-4404">
            {parentGroups.length > 0 && activeParentData && <>
                <MenuParentTabs parents={parentGroups.map(p => ({
              key: p.key,
              label: p.label,
              icon: p.icon
            }))} activeParent={activeParent} onSelectParent={handleSelectParent} subcategories={activeParentData.subcategories} activeSubcategory={activeSubcategory} onSelectSubcategory={handleSelectSubcategory} primaryColor={primaryColor} data-id="element-4405" />

                {/* Menu items — filtered by active subcategory */}
                <div className="mt-8" data-id="element-4406">
                  <AnimatePresence mode="wait" data-id="element-4407">
                    {(() => {
                  const activeCategory = activeParentData.subcategories.find(c => c.id === activeSubcategory);
                  if (!activeCategory) return null;
                  const filteredItems = items.filter(item => item.category_id === activeCategory.id);
                  if (filteredItems.length === 0) return null;
                  return <motion.div key={activeSubcategory} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} data-id="element-4408">
                          <div className="flex items-center gap-3 mb-2" data-id="element-4409">
                            <div className="w-1 h-5 rounded-full" style={{
                        backgroundColor: primaryColor
                      }} data-id="element-4410" />
                            <h3 className="text-base sm:text-lg font-bold text-white/80 tracking-tight" data-id="element-4411">
                              {activeCategory.name}
                            </h3>
                            <span className="text-xs text-white/20 font-medium" data-id="element-4412">
                              {filteredItems.length}
                            </span>
                          </div>
                          <div className="divide-y divide-white/[0.03]" data-id="element-4413">
                            {filteredItems.map(item => <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} data-id="element-4414" />)}
                          </div>
                        </motion.div>;
                })()}
                  </AnimatePresence>
                </div>
              </>}

            {/* Fallback */}
            {categories.length === 0 && items.length > 0 && <div className="divide-y divide-white/[0.03]" data-id="element-4415">
                {items.map(item => <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} data-id="element-4416" />)}
              </div>}
          </div>
        </div>
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-4417" />

      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-4418" />

      <Footer data-id="element-4419" />

      {/* Open Order Alert */}
      {showOpenOrderAlert && openOrder && <OpenOrderAlert order={openOrder} onDismiss={handleDismissOpenOrder} onViewTracking={handleCancelOpenOrder} data-id="element-4420" />}
    </div>;
}